import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AlertTriangle, Sparkles, ArrowRight, Share2, Plus } from "lucide-react";
import { formatMoney } from "../lib/constants";
import ShareCardDialog from "./ShareCardDialog";
import BuildingProfileNotice from "./BuildingProfileNotice";

/**
 * Auto-detects a trading pattern based on Trade Source P&L.
 * Logic:
 *   - 0–1 total trades → "Log a few trades…" empty nudge
 *   - ≥2 total trades → ALWAYS surface best/worst source.
 *       Prefer last-7-day data; if that window is empty, fall back to
 *       all-time `by_source` (the same data 'Performance by Source' uses).
 */
export default function PatternOfTheWeek({
  stats, currency,
  lowSample = false, tradesRemaining = 0,
  language = "en",
}) {
  const HI = language === "hi";
  const nav = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);
  const totalTrades = stats?.total_trades ?? 0;
  const weekList = stats?.weekly_source_stats || [];
  const allList = stats?.by_source || [];
  // Prefer the recent week; fall back to all-time so the banner is never empty
  // when at least 2 trades exist.
  const usingWeek = weekList.length > 0;
  const list = usingWeek ? weekList : allList;

  // ── Iter 36: Low trade-count guardrail ──
  // With <20 trades, "Pattern of the week" can lock onto a single emotional
  // outlier and mislead the user. Show a placeholder instead.
  if (lowSample) {
    return (
      <div
        data-testid="pattern-of-week-placeholder-card"
        className="relative border border-dashed border-[var(--bt-border)] rounded-sm p-5 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(0,229,160,0.04) 0%, rgba(18,18,20,1) 60%)",
        }}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          <div
            className="shrink-0 w-12 h-12 rounded-sm border flex items-center justify-center"
            style={{ borderColor: "var(--bt-accent)", color: "var(--bt-accent)" }}
          >
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="bt-label" style={{ color: "var(--bt-accent)" }}>
              Pattern of the week
            </p>
            <BuildingProfileNotice
              tradesRemaining={tradesRemaining}
              testId="pattern-of-week-placeholder"
            />
          </div>
          <Link
            to="/trades/new"
            data-testid="pattern-of-week-add-btn"
            className="inline-flex shrink-0 items-center gap-1.5 px-4 py-2.5 bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm text-sm transition-colors duration-150"
          >
            <Plus className="w-4 h-4" /> Log a trade
          </Link>
        </div>
      </div>
    );
  }

  // Empty-state nudge — only when we genuinely don't have enough trades.
  if (totalTrades < 2 || !list.length) {
    return (
      <div
        data-testid="pattern-of-week-empty"
        className="relative border border-dashed border-[var(--bt-border)] rounded-sm p-5 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(0,229,160,0.04) 0%, rgba(18,18,20,1) 60%)",
        }}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          <div
            className="shrink-0 w-12 h-12 rounded-sm border flex items-center justify-center"
            style={{ borderColor: "var(--bt-accent)", color: "var(--bt-accent)" }}
          >
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="bt-label" style={{ color: "var(--bt-accent)" }}>
              Pattern of the week
            </p>
            <h3 className="font-heading text-xl md:text-2xl mt-1 leading-tight">
              Log a few trades to see your pattern
            </h3>
            <p className="text-sm text-[var(--bt-text-muted)] mt-1.5">
              Once you have at least 2 trades, we'll surface the trade source that's costing or making you the most.
            </p>
          </div>
          <Link
            to="/trades/new"
            data-testid="pattern-of-week-add-btn"
            className="inline-flex shrink-0 items-center gap-1.5 px-4 py-2.5 bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm text-sm transition-colors duration-150"
          >
            <Plus className="w-4 h-4" /> Log a trade
          </Link>
        </div>
      </div>
    );
  }

  const pnlField = currency === "INR" ? "total_pnl_inr" : "total_pnl_usd";
  // Sort defensively so we don't rely on backend ordering being one direction.
  const sorted = [...list].sort((a, b) => a[pnlField] - b[pnlField]);
  const worst = sorted[0];
  const best = sorted[sorted.length - 1];
  const isWarning = worst[pnlField] < 0;
  const subject = isWarning ? worst : best;
  const pnl = subject[pnlField];
  const windowLabel = usingWeek ? "this week" : "so far";

  const startISO = stats?.week_start;
  const onViewTrades = () => {
    const params = new URLSearchParams({ source: subject.source });
    if (usingWeek && startISO) {
      const today = new Date().toISOString().slice(0, 10);
      params.set("start_date", startISO);
      params.set("end_date", today);
    }
    nav(`/trades?${params.toString()}`);
  };

  const Icon = isWarning ? AlertTriangle : Sparkles;
  const accentColor = isWarning ? "var(--bt-danger)" : "var(--bt-accent)";

  return (
    <>
      <div
        data-testid="pattern-of-week"
        data-card="pattern-banner"
        className="pattern-banner relative border rounded-sm p-5 overflow-hidden"
        style={{
          background: isWarning
            ? "linear-gradient(135deg, rgba(255,59,48,0.08) 0%, rgba(18,18,20,1) 60%)"
            : "linear-gradient(135deg, rgba(0,229,160,0.08) 0%, rgba(18,18,20,1) 60%)",
          borderColor: isWarning ? "rgba(255,59,48,0.35)" : "rgba(0,229,160,0.35)",
        }}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          <div
            className="shrink-0 w-12 h-12 rounded-sm border flex items-center justify-center"
            style={{ borderColor: accentColor, color: accentColor }}
          >
            <Icon className="w-5 h-5" />
          </div>

          <div className="flex-1 min-w-0">
            <p
              className="bt-label"
              style={{ color: accentColor }}
              data-testid="pattern-of-week-label"
            >
              {isWarning ? "Pattern of the week · Watch out" : "Pattern of the week"}
            </p>
            <h3 className="font-heading text-xl md:text-2xl mt-1 leading-tight">
              {isWarning ? (
                HI ? (
                  <>
                    Aapke <span style={{ color: accentColor }}>{subject.source}</span>{" "}
                    trades {windowLabel === "this week" ? "is week" : "abhi tak"} aapka account bleed kar rahe hain
                  </>
                ) : (
                  <>
                    Your <span style={{ color: accentColor }}>{subject.source}</span>{" "}
                    trades are bleeding your account {windowLabel}
                  </>
                )
              ) : (
                HI ? (
                  <>
                    <span style={{ color: accentColor }}>{subject.source}</span>{" "}
                    {windowLabel === "this week" ? "is week" : "abhi tak"} aapka strongest source hai
                  </>
                ) : (
                  <>
                    <span style={{ color: accentColor }}>{subject.source}</span>{" "}
                    is your strongest source {windowLabel}
                  </>
                )
              )}
            </h3>
            <p className="text-sm text-[var(--bt-text-muted)] mt-1.5">
              <span className="font-mono text-white">{subject.count}</span> trade
              {subject.count === 1 ? "" : "s"} ·{" "}
              <span
                className="font-mono font-semibold"
                style={{ color: pnl >= 0 ? "var(--bt-accent)" : "var(--bt-danger)" }}
              >
                {formatMoney(pnl, currency)}
              </span>{" "}
              · {HI ? "Win Rate" : "win rate"}{" "}
              <span className="font-mono text-white">{(subject.win_rate ?? 0).toFixed(2)}%</span>
              {subject.avg_r_multiple != null && (
                <>
                  {" "}· avg R:R{" "}
                  <span
                    className="font-mono font-semibold"
                    style={{
                      color:
                        subject.avg_r_multiple >= 0
                          ? "var(--bt-accent)"
                          : "var(--bt-danger)",
                    }}
                  >
                    1 : {subject.avg_r_multiple.toFixed(1)}
                  </span>
                </>
              )}
            </p>
          </div>

          <div className="flex flex-row gap-2 shrink-0">
            <button
              data-testid="pattern-of-week-share-btn"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-[var(--bt-border)] text-white text-sm font-bold rounded-sm transition-colors duration-150 hover:bg-white/5"
              title="Download shareable card"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button
              data-testid="pattern-of-week-view-btn"
              onClick={onViewTrades}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 border text-sm font-bold rounded-sm transition-colors duration-150 hover:bg-white/5"
              style={{ borderColor: accentColor, color: accentColor }}
            >
              View trades <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <ShareCardDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        stats={stats}
        currency={currency}
      />
    </>
  );
}
